import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const DocumentIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);

const ExternalIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5h5v5m0-5L10 14M5 5h4m-4 0v14h14v-4" />
  </svg>
);

const policies = [
  {
    title: 'NYCDOE Discipline Code',
    description: 'The citywide standards for student behavior, and the supports and consequences schools use to respond.',
    href: 'https://www.schools.nyc.gov/school-life/know-your-rights/discipline-code',
  },
  {
    title: 'NYCDOE Respect for All',
    description: 'How NYC Public Schools prevents and addresses bullying, harassment, intimidation, and discrimination.',
    href: 'https://www.schools.nyc.gov/school-life/school-environment/respect-for-all',
  },
  {
    title: 'NYCDOE Responding to Behavioral Crises',
    description: 'Guidance on how schools support students who are experiencing a behavioral crisis.',
    href: 'https://www.schools.nyc.gov/school-life/special-situations/responding-to-behavioral-crises',
  },
  {
    title: 'NYCDOE Internet Acceptable Use Policy',
    description: 'Rules for the safe and responsible use of school internet, devices, and online services.',
    href: 'https://www.schools.nyc.gov/about-us/policies/internet-acceptable-use-policy',
  },
];

const schoolDocuments = [
  {
    title: 'Respect for All: Family Resource',
    description: 'A two-page NYC Public Schools guide to recognizing bullying and harassment, how to report a concern, and free mental health support. PDF.',
    href: '/assets/documents/respect-for-all-family-resource.pdf',
  },
];

const FormsDocumentsPage = () => (
  <div className="min-h-screen bg-white">
    <Navbar />

    <main id="main-content" className="pt-0">
      {/* Hero */}
      <section className="relative pt-36 pb-20 bg-[#00213a] overflow-hidden">
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0"
          style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
          aria-hidden="true"
        />

        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <span className="text-sm font-semibold text-white tracking-tight">Resources</span>
            </div>
          </div>
          <h1 className="text-5xl lg:text-7xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Forms &amp; Documents
          </h1>
          <p className="text-2xl text-white/90 font-normal max-w-3xl leading-relaxed mx-auto">
            Important policies and documents for PS 96 students and families.
          </p>
        </div>
      </section>

      {/* NYCDOE policies */}
      <section className="max-w-5xl mx-auto px-6 lg:px-8 mt-12 mb-20">
        <h2 className="text-3xl font-semibold text-gray-900 mb-2 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
          NYC Public Schools Policies
        </h2>
        <p className="text-gray-600 mb-8">
          These links open the official NYC Public Schools website in a new tab.
        </p>
        <ul className="grid md:grid-cols-2 gap-6">
          {policies.map((doc) => (
            <li key={doc.href}>
              <a
                href={doc.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex gap-4 h-full bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md hover:border-[#fdbb2e]/40 transition-all duration-200"
              >
                <div className="w-12 h-12 rounded-lg bg-[#fdbb2e]/10 flex items-center justify-center text-[#00213a] flex-shrink-0">
                  <DocumentIcon />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#00213a] inline-flex items-center gap-2">
                    {doc.title} <ExternalIcon />
                  </h3>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">{doc.description}</p>
                  <span className="sr-only"> (opens in a new tab)</span>
                </div>
              </a>
            </li>
          ))}
        </ul>
      </section>

      {/* School documents */}
      <section className="max-w-5xl mx-auto px-6 lg:px-8 mb-20">
        <h2 className="text-3xl font-semibold text-gray-900 mb-2 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
          Family Resources
        </h2>
        <p className="text-gray-600 mb-8">
          Downloadable documents for PS 96 families. PDFs open in a new tab.
        </p>
        <ul className="grid md:grid-cols-2 gap-6">
          {schoolDocuments.map((doc) => (
            <li key={doc.href}>
              <a
                href={doc.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex gap-4 h-full bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md hover:border-[#fdbb2e]/40 transition-all duration-200"
              >
                <div className="w-12 h-12 rounded-lg bg-[#fdbb2e]/10 flex items-center justify-center text-[#00213a] flex-shrink-0">
                  <DocumentIcon />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#00213a] inline-flex items-center gap-2">
                    {doc.title} <ExternalIcon />
                  </h3>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">{doc.description}</p>
                  <span className="sr-only"> (PDF, opens in a new tab)</span>
                </div>
              </a>
            </li>
          ))}
        </ul>
      </section>
    </main>

    <Footer />
  </div>
);

export default FormsDocumentsPage;
